import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import ContentEditor from '../components/ContentEditor';
import WeeklySchedule from '../components/WeeklySchedule';
import WeeklyScheduleEditor from '../components/WeeklyScheduleEditor';
import EventsCalendar from '../components/EventsCalendar';
import EventsEditor from '../components/EventsEditor';
import { firestore, doc, getDoc } from '../firebase-config';
import '../styles/Home.css';
import DC_pastor_photo from '../assets/DC_pastor_photo.jpg';
import Layout from '../layouts/Layout';
import { Helmet } from 'react-helmet-async';

const Home = () => {
  const { loggedInUser } = useContext(AuthContext);
  const isAdmin = loggedInUser?.role === 'Admin';
  const [sections, setSections] = useState([]);
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        // Fetch page content
        const pageRef = doc(firestore, 'pageContent', 'home');
        const pageSnap = await getDoc(pageRef);
        if (pageSnap.exists()) {
          const data = pageSnap.data();
          if (data.sections) {
            setSections(data.sections);
          } else if (data.title || data.content) {
            // Backward compatibility for old data structure
            setSections([{ title: data.title || '', content: data.content || '<p></p>' }]);
          } else {
            setSections([{ title: '', content: '<p></p>' }]);
          }
        } else {
          setSections([{ title: '', content: '<p></p>' }]);
        }

        // Fetch weekly schedule
        const scheduleRef = doc(firestore, 'pageContent', 'weeklySchedule');
        const scheduleSnap = await getDoc(scheduleRef);
        if (scheduleSnap.exists()) {
          const scheduleData = scheduleSnap.data();
          setWeeklySchedule(scheduleData.schedule || []);
        }

        // Fetch events
        const eventsRef = doc(firestore, 'pageContent', 'events');
        const eventsSnap = await getDoc(eventsRef);
        if (eventsSnap.exists()) {
          const eventsData = eventsSnap.data();
          setEvents(eventsData.events || []);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleSave = (newSections) => {
    setSections(newSections);
  };

  const handleScheduleSave = (newSchedule) => {
    setWeeklySchedule(newSchedule);
  };

  const handleEventsSave = (newEvents) => {
    setEvents(newEvents);
  };

  if (loading) {
    return (
      <Layout featureType="home">
        <Helmet>
          <title>Crich Baptist Church – Reformed Baptist Church, Derbyshire</title>
        </Helmet>
        <div className="loading">
          Loading...
          <div className="skeleton" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout featureType="home">
      <Helmet>
        <title>Crich Baptist Church – Reformed Baptist Church, Derbyshire</title>
        <meta
          name="description"
          content="Crich Baptist Church in Crich, Derbyshire. A Christ-centred, Bible-believing evangelical church. Sunday services 10:45am & 6pm. Everyone welcome."
        />
        <meta property="og:title" content="Crich Baptist Church, Derbyshire" />
        <meta
          property="og:description"
          content="Reformed & Evangelical Baptist Church in Crich, Derbyshire. Sermons, events, and resources."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://crichbaptistchurch.org/" />
        <link rel="canonical" href="https://crichbaptistchurch.org/" />
      </Helmet>

      <div className="pastor-section">
        <img src={DC_pastor_photo} alt="Our Pastor, Darren Clarke" className="ryle-img" />
        <div className="plaque">Our Pastor, Darren Clarke</div>
      </div>

      {/* Main Content Sections */}
      {isAdmin ? (
        <ContentEditor
          pageId="home"
          initialSections={sections}
          onSave={handleSave}
        />
      ) : (
        <div className="sections">
          {sections.map((section, index) => (
            <div key={index} className="section">
              <h2>{section.title || `Section ${index + 1}`}</h2>
              <div dangerouslySetInnerHTML={{ __html: section.content }} />
            </div>
          ))}
        </div>
      )}

      {/* Weekly Schedule Section */}
      {isAdmin ? (
        <WeeklyScheduleEditor
          initialSchedule={weeklySchedule}
          onSave={handleScheduleSave}
        />
      ) : (
        <WeeklySchedule schedule={weeklySchedule} />
      )}

      {/* Special Events Calendar/Editor */}
      {isAdmin ? (
        <EventsEditor
          initialEvents={events}
          onSave={handleEventsSave}
        />
      ) : (
        <EventsCalendar events={events} />
      )}
    </Layout>
  );
};

export default Home;